/**
 * use-composer-agents — data source for the UNIFIED Ask-Argus composer picker.
 *
 * askargus bug #7 — the locked ASK-ARGUS-UI-SPEC §B.3.3 mandates a SINGLE
 * Agent picker on the composer's bottom-left with two grouped sections:
 *
 *   - "Models"      → published SYSTEM agents (sysadmin-authored chat brains
 *                     like Ask Argus / Argus Code). Rendered under the
 *                     audience-aware "Models" label per CATALOG-ARCHITECTURE §2.
 *   - "Your agents" → tenant-authored PUBLISHED agents.
 *
 * The old composer rendered TWO parallel pickers (a tenant-agent "Select
 * Agent" dropdown AND a separate four-brand "Select Model" dropdown). That
 * diverged from the spec and confused users about which control drove the
 * answer. This hook backs the replacement single picker by reading the
 * server-side unified endpoint `GET /custom-agents/available`, which already
 * filters drafts / disabled / soft-deleted / QA-junk rows so the picker never
 * surfaces a non-runnable selection.
 *
 * Selection semantics (see chat-input.tsx handleSubmit):
 *   - System "Model" row  → submit `agent = row.slug` (so the pipeline matches
 *                           BUILTIN_AGENTS) AND `model = row.brandId` (so the
 *                           engine dispatch is honored, exactly like @mention).
 *   - Tenant agent row    → submit `agent = row.id`, `model` left to default.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/** One row in the unified composer picker (system "Model" or tenant agent). */
export interface ComposerAgentRow {
  /** Slug for system rows (BUILTIN_AGENTS key); UUID id for tenant rows. */
  id: string;
  slug: string | null;
  name: string;
  description: string | null;
  /** Lucide icon name (e.g. 'bot') — resolve via getIconByName. */
  icon: string;
  /** True = sysadmin-authored "Model"; false = tenant-authored agent. */
  isSystem: boolean;
  /** rev_label / version subtitle (e.g. "1.4"). Null when unset. */
  revLabel: string | null;
  /** Light pin affordance. */
  pinned: boolean;
  /**
   * Engine brand id to dispatch on (system rows only). Threaded into the chat
   * request `brand` so picking a different Model actually changes the engine.
   * Null for tenant agents.
   */
  brandId: string | null;
}

interface AvailableAgentsResponse {
  models: ComposerAgentRow[];
  agents: ComposerAgentRow[];
}

export interface ComposerAgentsData {
  /** "Models" group — published system agents. */
  models: ComposerAgentRow[];
  /** "Your agents" group — tenant published agents. */
  agents: ComposerAgentRow[];
  /** Flat list (models first, then tenant agents) for selection lookup. */
  all: ComposerAgentRow[];
  isLoading: boolean;
}

/**
 * Fetch the unified picker rows. Shares a query key namespace with the
 * agents hub (`['custom-agents', ...]`) so a publish / unpublish elsewhere
 * invalidates this picker too.
 */
export function useComposerAgents(): ComposerAgentsData {
  const q = useQuery<AvailableAgentsResponse>({
    queryKey: ['custom-agents', 'available'],
    queryFn: () =>
      apiClient.get<AvailableAgentsResponse>('/custom-agents/available'),
    // The composer is the first thing a user touches; keep the picker warm
    // for a minute so re-opening the dropdown is instant.
    staleTime: 60_000,
  });

  const models = q.data?.models ?? [];
  const agents = q.data?.agents ?? [];
  return {
    models,
    agents,
    all: [...models, ...agents],
    isLoading: q.isLoading,
  };
}

/**
 * Resolve the chat-submit `{ agent, model }` pair for a picked row.
 *
 *   - System "Model" → agent = slug (pipeline resolves via BUILTIN_AGENTS),
 *                      model = brandId (engine dispatch).
 *   - Tenant agent   → agent = id, model = '' (default brand).
 */
export function resolveComposerSelection(
  row: ComposerAgentRow | null | undefined,
): { agent: string; model: string } {
  if (!row) return { agent: '', model: '' };
  if (row.isSystem) {
    return { agent: row.slug ?? row.id, model: row.brandId ?? '' };
  }
  return { agent: row.id, model: '' };
}
